/**
 * @voltxampere/web — `<vxa-command-palette>` (Sprint 22 C2)
 *
 * Cmd+K modal — the BLUEPRINT §14 surface.
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ KOMUT PALETI                            ESC  │
 *   ├──────────────────────────────────────────────┤
 *   │ [search ____________________________]        │
 *   │ ── Recent ─────────────────────────────      │
 *   │   Run DC                              F5     │
 *   │ ── Suggested ───────────────────────────     │
 *   │   Voltaj bölücü                              │
 *   │   Save circuit                       Cmd+S   │
 *   └──────────────────────────────────────────────┘
 *
 * The empty-query state shows two sections (Recent + Suggested);
 * any non-empty query swaps the body for the fuzzy-ranked search
 * result list. Match characters are highlighted via per-char span
 * wrap from `CommandRegistry.search`'s `matchedIndices`.
 *
 * Keyboard:
 *   - typing: updates `paletteQuery`, clamps `selectedCommandIndex`
 *   - ↑ / ↓: navigate the visible list (wraps both ends)
 *   - Enter: executes the highlighted command and closes
 *   - Esc:  handled by `shortcuts/catalog.ts` (closes the modal)
 *
 * Async execution surfaces via `paletteExecuting` so a
 * long-running command (export, IDB read) can show a spinner
 * row without blocking re-opens.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, html, css, nothing, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";

import { commandPaletteOpen } from "../store/ui.js";
import { currentLocale } from "../store/locale.js";
import {
  paletteExecuting,
  paletteQuery,
  recentCommandIds,
  resetPaletteState,
  selectedCommandIndex,
} from "../store/palette.js";
import { recordAndPersistRecent } from "../commands/recent.js";
import { commandRegistry } from "../commands/registry.js";
import { type Command } from "../commands/types.js";
import { t, type TranslationKey } from "../i18n/index.js";
import { SignalController } from "../lit-utils/signal-controller.js";

const SUGGESTED_LIMIT = 8;

interface ResultRow {
  readonly command: Command;
  readonly matchedIndices: readonly number[];
  readonly section: "search" | "recent" | "suggested";
}

@customElement("vxa-command-palette")
export class VxaCommandPalette extends LitElement {
  private readonly _open = new SignalController(this, commandPaletteOpen);
  private readonly _locale = new SignalController(this, currentLocale);
  private readonly _query = new SignalController(this, paletteQuery);
  private readonly _selected = new SignalController(this, selectedCommandIndex);
  private readonly _executing = new SignalController(this, paletteExecuting);
  private readonly _recents = new SignalController(this, recentCommandIds);

  /** Snapshot of the rows the user can act on, recomputed on render. */
  @state() private rows: readonly ResultRow[] = [];

  private wasOpen = false;

  static override styles = css`
    :host {
      position: fixed;
      inset: 0;
      z-index: var(--z-modal);
      display: none;
      align-items: flex-start;
      justify-content: center;
      background: rgba(3, 5, 10, 0.7);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
    }

    :host([data-open]) {
      display: flex;
    }

    .panel {
      margin-top: 80px;
      width: min(640px, 92vw);
      max-height: min(560px, 80vh);
      background: var(--ink);
      border: 1px solid var(--ivory-wisp);
      box-shadow: 0 0 0 1px var(--brass-ghost);
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .header {
      padding: var(--s-3) var(--s-5);
      border-bottom: 1px solid var(--ivory-wisp);
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .heading {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.28em;
      color: var(--brass);
      text-transform: uppercase;
    }

    .hint {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.18em;
      color: var(--ivory-faint);
      text-transform: uppercase;
    }

    .search {
      width: calc(100% - 2 * var(--s-4));
      background: var(--void);
      border: 1px solid var(--ivory-wisp);
      padding: var(--s-3) var(--s-4);
      font-family: var(--font-mono);
      font-size: var(--fs-small);
      color: var(--ivory);
      outline: none;
      letter-spacing: 0.02em;
      margin: var(--s-4);
    }

    .search::placeholder {
      color: var(--ivory-faint);
      font-style: italic;
      font-family: var(--font-body);
      letter-spacing: 0;
    }

    .results {
      flex: 1 1 auto;
      overflow-y: auto;
      padding-bottom: var(--s-3);
    }

    .section-header {
      padding: var(--s-2) var(--s-5);
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.22em;
      color: var(--ivory-faint);
      text-transform: uppercase;
      border-top: 1px solid var(--ivory-wisp);
      background: var(--void);
    }

    .row {
      padding: var(--s-3) var(--s-5);
      display: flex;
      align-items: center;
      justify-content: space-between;
      cursor: pointer;
      color: var(--ivory);
      font-family: var(--font-body);
      font-size: var(--fs-body);
      gap: var(--s-3);
    }

    .row[data-selected="true"] {
      background: var(--brass-ghost);
      color: var(--ivory-bright);
    }

    .row:hover {
      background: var(--brass-ghost);
    }

    .row .title {
      flex: 1 1 auto;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .row .title .match {
      color: var(--brass);
      font-weight: 600;
    }

    .row .description {
      flex: 0 1 auto;
      max-width: 40%;
      color: var(--ivory-dim);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.04em;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .row .shortcut {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.1em;
      color: var(--ivory-faint);
      flex: 0 0 auto;
    }

    .row[data-executing="true"] .shortcut {
      color: var(--brass);
      animation: pulse 0.9s ease-in-out infinite;
    }

    @keyframes pulse {
      0%,
      100% {
        opacity: 0.4;
      }
      50% {
        opacity: 1;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .row[data-executing="true"] .shortcut {
        animation: none;
        opacity: 1;
      }
    }

    .empty {
      padding: var(--s-7) var(--s-5);
      text-align: center;
      color: var(--ivory-dim);
      font-family: var(--font-body);
      font-style: italic;
    }

    .empty .empty-title {
      font-style: normal;
      color: var(--ivory);
      margin-bottom: var(--s-2);
      letter-spacing: 0.04em;
    }
  `;

  override connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener("click", this._onBackdrop as EventListener);
  }

  override disconnectedCallback(): void {
    this.removeEventListener("click", this._onBackdrop as EventListener);
    super.disconnectedCallback();
  }

  override updated(): void {
    this.toggleAttribute("data-open", this._open.value);

    // Reset query + autofocus on transition closed → open. The
    // autofocus call is a no-op in unit-test environments where
    // happy-dom does not implement focus(); the visual focus is
    // best-effort.
    if (this._open.value && !this.wasOpen) {
      resetPaletteState();
      const search = this.shadowRoot?.querySelector<HTMLInputElement>(".search");
      if (search) {
        search.focus();
      }
    }
    this.wasOpen = this._open.value;
  }

  private readonly _onBackdrop = (e: MouseEvent): void => {
    if (e.target === this) {
      commandPaletteOpen.value = false;
    }
  };

  private readonly _onSearchInput = (e: Event): void => {
    const target = e.target as HTMLInputElement;
    paletteQuery.value = target.value;
    selectedCommandIndex.value = 0;
  };

  private readonly _onSearchKeyDown = (e: KeyboardEvent): void => {
    const count = this.rows.length;
    if (count === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      selectedCommandIndex.value = (this._selected.value + 1) % count;
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      selectedCommandIndex.value = (this._selected.value - 1 + count) % count;
    } else if (e.key === "Enter") {
      e.preventDefault();
      const idx = this._selected.value;
      const row = this.rows[idx];
      if (row) {
        void this._runCommand(row.command.id);
      }
    }
  };

  private async _runCommand(id: string): Promise<void> {
    paletteExecuting.value = id;
    try {
      await commandRegistry.execute(id);
      recordAndPersistRecent(id);
      commandPaletteOpen.value = false;
    } finally {
      paletteExecuting.value = null;
    }
  }

  private _resolveRows(): readonly ResultRow[] {
    const query = this._query.value;

    if (query.length > 0) {
      return commandRegistry.search(query, { resolveKey: tForKey }).map((r) => ({
        command: r.command,
        matchedIndices: r.matchedIndices,
        section: "search" as const,
      }));
    }

    const rows: ResultRow[] = [];

    // Recent — newest first; ids may be stale (e.g. dev unregistered
    // a command), so resolve through the registry and skip misses.
    for (const id of this._recents.value) {
      const cmd = commandRegistry.getById(id);
      if (!cmd) continue;
      if (cmd.when && !cmd.when()) continue;
      rows.push({ command: cmd, matchedIndices: [], section: "recent" });
    }

    // Suggested — first N from the registry's insertion order
    // (file > edit > view…), excluding any command already shown
    // in Recent so we never show the same row twice.
    const recentSet = new Set(rows.map((r) => r.command.id));
    let suggestedCount = 0;
    for (const cmd of commandRegistry.list()) {
      if (suggestedCount >= SUGGESTED_LIMIT) break;
      if (recentSet.has(cmd.id)) continue;
      if (cmd.when && !cmd.when()) continue;
      rows.push({ command: cmd, matchedIndices: [], section: "suggested" });
      suggestedCount += 1;
    }

    return rows;
  }

  override render() {
    if (!this._open.value) return nothing;

    // Recompute rows up-front so render + keyboard handlers share
    // the same snapshot.
    this.rows = this._resolveRows();

    // Clamp selectedIndex when the list shrinks (e.g. user typed a
    // narrower query that filtered out the previously highlighted
    // row).
    const count = this.rows.length;
    if (count > 0 && this._selected.value >= count) {
      selectedCommandIndex.value = count - 1;
    }

    const body = this._renderBody();
    return html`
      <div
        class="panel"
        @click=${(e: Event) => {
          e.stopPropagation();
        }}
      >
        <div class="header">
          <span class="heading">${t("palette.title")}</span>
          <span class="hint">${t("palette.hint")}</span>
        </div>
        <input
          class="search"
          type="text"
          .value=${this._query.value}
          placeholder=${t("palette.search")}
          aria-label=${t("palette.search")}
          @input=${this._onSearchInput}
          @keydown=${this._onSearchKeyDown}
        />
        <div class="results">${body}</div>
      </div>
    `;
  }

  private _renderBody(): readonly (TemplateResult | typeof nothing)[] {
    if (this.rows.length === 0) {
      return [
        html`
          <div class="empty">
            <div class="empty-title">${t("palette.empty.title")}</div>
            <div>${t("palette.empty.hint")}</div>
          </div>
        `,
      ];
    }

    if (this._query.value.length > 0) {
      return this.rows.map((row, i) => this._renderRow(row, i));
    }

    // Empty-query layout: Recent + Suggested sections.
    const recentRows = this.rows.filter((r) => r.section === "recent");
    const suggestedRows = this.rows.filter((r) => r.section === "suggested");
    const out: (TemplateResult | typeof nothing)[] = [];
    let visualIndex = 0;

    if (recentRows.length > 0) {
      out.push(html`<div class="section-header">${t("palette.recent.label")}</div>`);
      for (const row of recentRows) {
        out.push(this._renderRow(row, visualIndex));
        visualIndex += 1;
      }
    }
    if (suggestedRows.length > 0) {
      out.push(html`<div class="section-header">${t("palette.suggested.label")}</div>`);
      for (const row of suggestedRows) {
        out.push(this._renderRow(row, visualIndex));
        visualIndex += 1;
      }
    }
    return out;
  }

  private _renderRow(row: ResultRow, visualIndex: number): TemplateResult {
    const isSelected = visualIndex === this._selected.value;
    const isExecuting = this._executing.value === row.command.id;
    const title = tForKey(row.command.titleKey);
    const description = row.command.descriptionKey ? tForKey(row.command.descriptionKey) : "";
    const shortcut = isExecuting ? t("palette.executing") : (row.command.shortcut ?? "");

    return html`
      <div
        class="row"
        data-selected=${isSelected ? "true" : "false"}
        data-executing=${isExecuting ? "true" : "false"}
        data-command-id=${row.command.id}
        @click=${() => {
          void this._runCommand(row.command.id);
        }}
        @mouseenter=${() => {
          selectedCommandIndex.value = visualIndex;
        }}
      >
        <span class="title">${this._renderHighlightedTitle(title, row.matchedIndices)}</span>
        ${description.length > 0 ? html`<span class="description">${description}</span>` : nothing}
        <span class="shortcut">${shortcut}</span>
      </div>
    `;
  }

  private _renderHighlightedTitle(
    title: string,
    indices: readonly number[],
  ): readonly (string | TemplateResult)[] {
    if (indices.length === 0) return [title];
    const set = new Set(indices);
    const out: (string | TemplateResult)[] = [];
    for (let i = 0; i < title.length; i++) {
      const ch = title[i] ?? "";
      if (set.has(i)) {
        out.push(html`<span class="match">${ch}</span>`);
      } else {
        out.push(ch);
      }
    }
    return out;
  }
}

/**
 * Cast helper — `Command.titleKey` is currently typed as a free
 * `string` so plug-in commands (Sprint 23+) don't have to round-trip
 * a TR/EN bundle migration. Builtin commands all use real
 * `TranslationKey` values; for unknown keys (test harnesses, future
 * plug-ins) we fall back to the literal id so the palette never
 * scores fuzzy over `undefined`.
 */
function tForKey(key: string): string {
  // `t()` is typed to return `string` but performs a raw bundle
  // lookup that yields `undefined` for keys outside the compile-time
  // union. The cast widens the return so we can detect the gap.
  const resolved = t(key as TranslationKey) as string | undefined;
  return resolved ?? key;
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-command-palette": VxaCommandPalette;
  }
}
